import type { IEventBus } from "@/domains/events"
import type { TokenStore } from "@/domains/tokens"

import type { ISwapInputStore, ISwapQuoteStore } from "./interfaces"
import type { SwapQuoteRequestData } from "./types"
import { getSwapActionMode } from "./swap-mode"
import { SwapQuoteRequestClearedEvent, SwapQuoteRequestedEvent } from "./events"

/**
 * Interaction type for quote request orchestration
 */
export type QuoteInteractionType = "slider" | "typing" | "button" | "balance-click" | "token-change"

/**
 * Extended quote request data with hash and ID for plugin cancellation
 */
interface ExtendedQuoteRequestData extends SwapQuoteRequestData {
  requestHash: string
  requestId: number
}

/**
 * Swap Inputs Orchestrator
 * Intelligently manages quote requests based on user interaction type
 *
 * Rules:
 * - If no quote exists → send immediately
 * - If quote exists → cache for 3-600ms
 * - Slider: wait until deselected, then 120ms, then send only last value
 * - Typing: debounce 300ms
 * - Button clicks: send immediately
 * - Balance clicks: send immediately (tested independently)
 * - Cancel existing quotes when new ones come in (via request hash/ID)
 * - Treat zero same as empty string
 */
export class SwapInputsOrchestrator {
  // Interaction state
  private sliderPendingAmount: string | null = null
  private sliderTimeoutId: ReturnType<typeof setTimeout> | null = null

  // Typing state
  private typingTimeoutId: ReturnType<typeof setTimeout> | null = null

  // Quote caching state
  private lastQuoteRequest: SwapQuoteRequestData | null = null
  private lastRequestHash: string | null = null
  private lastQuoteRequestTime: number = 0
  private readonly cacheWindowMin: number = 3 // Minimum cache window (ms)
  private readonly cacheWindowMax: number = 600 // Maximum cache window (ms)

  // Request tracking
  private requestIdCounter: number = 0

  constructor(
    private readonly inputStore: ISwapInputStore,
    private readonly quoteStore: ISwapQuoteStore,
    private readonly eventBus: IEventBus,
    private readonly chainId: number,
    private readonly nativeToken: TokenStore,
    private readonly wrappedNativeToken: TokenStore,
    private readonly getSlippageBps: () => number,
  ) {}

  /**
   * Handle input amount change from UI
   * Determines interaction type and applies appropriate debouncing strategy
   * Does NOT update inputStore - UI handles that for immediate responsiveness
   */
  handleInputAmountChange(amount: string, interactionType: QuoteInteractionType = "typing"): void {
    // Normalize zero values to empty string
    const normalizedAmount = this.normalizeAmount(amount)

    // Handle zero/empty amounts
    if (!normalizedAmount || normalizedAmount === "" || normalizedAmount === "0") {
      this.handleEmptyAmount()
      return
    }

    // Route to appropriate handler based on interaction type
    switch (interactionType) {
      case "slider":
        this.handleSliderChange(normalizedAmount)
        break
      case "typing":
        this.handleTypingChange(normalizedAmount)
        break
      case "button":
        this.handleButtonClick(normalizedAmount)
        break
      case "balance-click":
        this.handleBalanceClick(normalizedAmount)
        break
      case "token-change":
        this.handleTokenChange(normalizedAmount)
        break
    }
  }

  /**
   * Handle slider interaction start
   * Called when slider becomes active
   */
  handleSliderStart(): void {
    // Cancel any pending typing debounce
    this.cancelTypingDebounce()
    this.cancelSliderDebounce()
  }

  /**
   * Handle slider interaction end
   * Called when slider is deselected/released
   * Sends the stored slider value immediately
   */
  handleSliderEnd(): void {
    if (this.sliderPendingAmount !== null) {
      const pendingAmount = this.sliderPendingAmount
      this.sliderPendingAmount = null
      this.cancelSliderDebounce()
      this.sliderTimeoutId = setTimeout(() => {
        this.sendQuoteRequest(pendingAmount, "slider")
        this.sliderTimeoutId = null
      }, 120)
    }
  }

  /**
   * Handle token change (input or output token)
   * Sends quote request immediately if amount exists
   */
  handleTokenChange(amount: string | undefined = undefined): void {
    const amountToUse = amount ?? this.inputStore.inputAmount
    const normalizedAmount = this.normalizeAmount(amountToUse)

    if (!normalizedAmount || normalizedAmount === "" || normalizedAmount === "0") {
      this.handleEmptyAmount()
      return
    }

    // Token changes send immediately (no debounce)
    this.sendQuoteRequest(normalizedAmount, "token-change")
  }

  /**
   * Normalize amount: treat zero same as empty string
   */
  private normalizeAmount(amount: string): string {
    if (!amount || amount.trim() === "" || amount === "0" || amount === "0.0") {
      return ""
    }
    return amount.trim()
  }

  /**
   * Handle empty amount
   */
  private handleEmptyAmount(): void {
    // Clear all pending requests
    this.cancelAllPendingRequests()

    // Clear quotes
    this.quoteStore.clearAggregator()

    // Emit clear event
    this.eventBus.publish(new SwapQuoteRequestClearedEvent())

    // Reset state
    this.lastQuoteRequest = null
    this.lastRequestHash = null
    this.lastQuoteRequestTime = 0
  }

  /**
   * Handle slider change
   * Stores the latest slider value - no debouncing needed
   * Quote will be sent when handleSliderEnd() is called
   */
  private handleSliderChange(amount: string): void {
    // Just store the latest slider value
    // No debouncing - we'll send when slider is released
    this.sliderPendingAmount = amount
  }

  /**
   * Handle typing change
   * Debounces for 300ms
   */
  private handleTypingChange(amount: string): void {
    // Cancel any existing typing debounce
    this.cancelTypingDebounce()

    // Set new debounce timer
    this.typingTimeoutId = setTimeout(() => {
      this.sendQuoteRequest(amount, "typing")
      this.typingTimeoutId = null
    }, 300)
  }

  /**
   * Handle button click (25%, 50%, etc.)
   * Sends immediately, no debounce
   */
  private handleButtonClick(amount: string): void {
    // Cancel any pending requests
    this.cancelAllPendingRequests()

    // Send immediately
    this.sendQuoteRequest(amount, "button")
  }

  /**
   * Handle balance click (Max button)
   * Sends immediately, no debounce (same as button but tested independently)
   */
  private handleBalanceClick(amount: string): void {
    // Cancel any pending requests
    this.cancelAllPendingRequests()

    // Send immediately
    this.sendQuoteRequest(amount, "balance-click")
  }

  /**
   * Send quote request with intelligent caching
   */
  private sendQuoteRequest(amount: string, interactionType: QuoteInteractionType): void {
    // Skip quote requests for wrap/unwrap - use WETH contract directly
    const actionMode = getSwapActionMode(
      this.inputStore.inputToken,
      this.inputStore.outputToken,
      this.nativeToken,
      this.wrappedNativeToken,
    )
    if (actionMode === "wrap" || actionMode === "unwrap") {
      this.quoteStore.clearAggregator()
      this.eventBus.publish(new SwapQuoteRequestClearedEvent())
      return
    }

    // Validate amount
    const normalizedAmount = this.normalizeAmount(amount)
    if (!normalizedAmount || normalizedAmount === "" || normalizedAmount === "0") {
      return
    }

    // Tokens are always set (defaults: ETH and SCALE), no need to check for undefined
    // Build quote request data
    // Tokens are guaranteed to be set (defaults: ETH and SCALE)
    const quoteRequest: SwapQuoteRequestData = {
      inputToken: this.inputStore.inputToken!,
      outputToken: this.inputStore.outputToken!,
      inputAmount: normalizedAmount,
      slippageBps: this.getSlippageBps(),
      chainId: this.chainId,
      nativeToken: this.nativeToken,
    }

    // Compute request hash for comparison
    const requestHash = this.computeRequestHash(quoteRequest)

    // Check if we should use cache
    const now = Date.now()
    const timeSinceLastRequest = now - this.lastQuoteRequestTime
    const hasExistingQuote = this.quoteStore.allDisplayQuotes.length > 0
    const isSameRequest = this.lastRequestHash === requestHash

    // If no quote exists, send immediately
    if (!hasExistingQuote) {
      this.emitQuoteRequest(quoteRequest, requestHash)
      return
    }

    // If quote exists, check cache window
    if (hasExistingQuote && isSameRequest) {
      // If within cache window, don't send (use cached quote)
      if (timeSinceLastRequest < this.cacheWindowMin) {
        return
      }

      // If beyond cache window but within max, still use cache but refresh
      if (timeSinceLastRequest < this.cacheWindowMax) {
        // Optionally refresh in background, but for now just use cache
        return
      }
    }

    // Send new quote request
    this.emitQuoteRequest(quoteRequest, requestHash)
  }

  /**
   * Compute unique hash from quote request parameters
   * Used by plugins to identify if request is the same or different
   */
  private computeRequestHash(request: SwapQuoteRequestData): string {
    // Tokens are guaranteed to be set
    const inputTokenAddress = request.inputToken!.address.toString()
    const outputTokenAddress = request.outputToken!.address.toString()
    const parts = [
      inputTokenAddress,
      outputTokenAddress,
      request.inputAmount,
      request.slippageBps.toString(),
      request.chainId.toString(),
    ]
    return parts.join("|")
  }

  /**
   * Emit quote request event with hash and ID
   * Increments request ID for cancellation tracking
   */
  private emitQuoteRequest(quoteRequest: SwapQuoteRequestData, requestHash: string): void {
    // Increment request ID for cancellation tracking
    this.requestIdCounter += 1
    const requestId = this.requestIdCounter

    // Update last request state
    this.lastQuoteRequest = quoteRequest
    this.lastRequestHash = requestHash
    this.lastQuoteRequestTime = Date.now()

    // Create extended request with hash and ID
    // Note: We extend the event payload, not the type definition
    const extendedRequest = {
      ...quoteRequest,
      requestHash,
      requestId,
    } as ExtendedQuoteRequestData

    // Emit event with extended data
    // Plugins can access requestHash and requestId from the event
    this.eventBus.publish(new SwapQuoteRequestedEvent(extendedRequest as any))
  }

  /**
   * Cancel all pending requests
   */
  private cancelAllPendingRequests(): void {
    this.cancelTypingDebounce()
    this.cancelSliderDebounce()
    this.sliderPendingAmount = null
  }

  /**
   * Cancel typing debounce
   */
  private cancelTypingDebounce(): void {
    if (this.typingTimeoutId) {
      clearTimeout(this.typingTimeoutId)
      this.typingTimeoutId = null
    }
  }

  private cancelSliderDebounce(): void {
    if (this.sliderTimeoutId) {
      clearTimeout(this.sliderTimeoutId)
      this.sliderTimeoutId = null
    }
  }
}
